/** Browser event emitted when the backend says the current agent lost access
 * to a specific Inbox conversation. Keeping the detection beside the feature
 * makes the flat backend error contract testable without coupling UI code to
 * generated SDK error types. */
export const INBOX_CONVERSATION_FORBIDDEN_EVENT =
  'blazeteams:inbox-conversation-forbidden';

export interface InboxConversationForbiddenDetail {
  conversationId: string;
  message: string;
}

/** A selected conversation must be released when it disappeared (404) or the
 * strict assignment gate moved it outside the current agent's scope (403). */
export function shouldReleaseConversation(status: number): boolean {
  return status === 403 || status === 404;
}

function normalizeMessage(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/** Parse the contract's flat 403 envelope for per-conversation access loss.
 * Other 403s (for example missing forward permission) deliberately do not
 * evict the active conversation. */
export function getConversationForbiddenDetail(
  requestUrl: string,
  payload: unknown,
): InboxConversationForbiddenDetail | null {
  if (!payload || typeof payload !== 'object') return null;
  const body = payload as { error?: unknown; code?: unknown; message?: unknown };
  if (body.error !== 'forbidden' || typeof body.message !== 'string') return null;

  if (body.code !== 'conversation_access_denied') {
    if (body.code != null) return null;
    // Compatibility for the exact historical access family only. Native
    // detection never parses translated prose or general permission failures.
    const normalized = normalizeMessage(body.message);
    if (!normalized.includes('permiso') || !normalized.includes('conversacion')
      || !normalized.includes('acceder') && !normalized.includes('ver esta')) return null;
  }

  const match = requestUrl.match(
    /\/inbox\/conversations\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:[/?#]|$)/i,
  );
  if (!match) return null;
  return { conversationId: match[1], message: body.message };
}

export function isTrulyUnassigned(conversation: {
  assigned_user_id?: string;
  assigned_team_id?: string;
}): boolean {
  return !conversation.assigned_user_id && !conversation.assigned_team_id;
}

/** The strict-assignment contract only removes "take" from unassigned work
 * for agents. Team-assigned work remains visible and may still be claimed by
 * a team member; supervisors with view_all retain the unassigned flow. */
export function canSelfAssignConversation(args: {
  assignedUserId?: string;
  assignedTeamId?: string;
  currentUserId?: string;
  canViewAll: boolean;
  isClosed: boolean;
}): boolean {
  if (
    args.isClosed ||
    !args.currentUserId ||
    args.assignedUserId === args.currentUserId
  ) {
    return false;
  }
  const unassigned = isTrulyUnassigned({
    assigned_user_id: args.assignedUserId,
    assigned_team_id: args.assignedTeamId,
  });
  return !unassigned || args.canViewAll;
}
